import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Icon } from '../common';
import { useGetLocation, useRouteNavigate } from '../../hooks';
import { addPropertyEvent, postListingMenuImpressionEvent } from '../../services/analyticsService';
import { PAGE_SECTIONS } from '../../services/analyticsService/constants';
import { getPostListingOptions } from './post-listing-options';
import {
  BodyScrim,
  ChromeScrim,
  RailScrim,
  MenuCaret,
  MenuPanel,
  MenuPositioner,
  MenuRow,
  NewChip,
  RowIcon,
  RowLabel,
  RowLead,
  RowTrailing,
} from './styled';

export const POST_LISTING_MENU_PLACEMENTS = {
  TOP_NAV: 'top_nav',
  LEFT_NAV: 'left_nav',
};

/** Lets the pointer cross the gap between the trigger and the panel without the panel closing. */
const CLOSE_DELAY = 150;

/** How long the menu has to stay open before it counts as seen. */
const IMPRESSION_DELAY = 300;

/**
 * One placement open at a time, shared across the header and the rail — they live in different
 * subtrees, so the open placement is held here rather than in either component.
 */
let openPlacement = null;
const subscribers = new Set();

const setOpenPlacement = (next) => {
  openPlacement = next;
  subscribers.forEach((notify) => notify(next));
};

export const usePostListingMenuPlacement = () => {
  const [placement, setPlacement] = useState(openPlacement);

  useEffect(() => {
    subscribers.add(setPlacement);
    setPlacement(openPlacement);
    return () => {
      subscribers.delete(setPlacement);
    };
  }, []);

  return placement;
};

export const usePostListingMenu = (placement) => {
  const activePlacement = usePostListingMenuPlacement();
  const open = activePlacement === placement;
  const closeTimer = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [focusFirstRow, setFocusFirstRow] = useState(false);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openMenu = useCallback(
    ({ withKeyboard = false } = {}) => {
      cancelScheduledClose();
      setFocusFirstRow(withKeyboard);
      setOpenPlacement(placement);
    },
    [cancelScheduledClose, placement],
  );

  const closeMenu = useCallback(
    ({ restoreFocus = false } = {}) => {
      cancelScheduledClose();
      setFocusFirstRow(false);
      if (openPlacement === placement) setOpenPlacement(null);
      if (restoreFocus) {
        // triggerRef holds the wrapper, since the shared Button is not a ref-forwarding component.
        const node = triggerRef.current;
        (node?.matches?.('button') ? node : node?.querySelector?.('button'))?.focus?.();
      }
    },
    [cancelScheduledClose, placement],
  );

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      if (openPlacement === placement) setOpenPlacement(null);
    }, CLOSE_DELAY);
  }, [cancelScheduledClose, placement]);

  const toggleMenu = useCallback(() => {
    if (open) closeMenu();
    else openMenu();
  }, [closeMenu, open, openMenu]);

  // Clear the shared state if this placement unmounts while open (e.g. a resize across the
  // mobile breakpoint), so the scrim cannot outlive its trigger.
  useEffect(
    () => () => {
      cancelScheduledClose();
      if (openPlacement === placement) setOpenPlacement(null);
    },
    [cancelScheduledClose, placement],
  );

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu({ restoreFocus: true });
    };
    const onPointerDown = (event) => {
      const insideTrigger = triggerRef.current?.contains?.(event.target);
      const insidePanel = panelRef.current?.contains?.(event.target);
      if (!insideTrigger && !insidePanel) closeMenu();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [closeMenu, open]);

  const onTriggerKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      openMenu({ withKeyboard: true });
    },
    [openMenu],
  );

  return {
    open,
    anyOpen: !!activePlacement,
    activePlacement,
    focusFirstRow,
    triggerRef,
    panelRef,
    openMenu,
    closeMenu,
    scheduleClose,
    cancelScheduledClose,
    toggleMenu,
    onTriggerKeyDown,
    triggerAriaProps: { 'aria-haspopup': 'menu', 'aria-expanded': open },
  };
};

/**
 * One impression per page view per placement. Both live at module scope for the same reason the
 * open placement does: the header and the rail are separate subtrees, and a remount of either
 * must not count as a new page view.
 */
let impressionPageKey = null;
const firedImpressions = new Set();

const useMenuImpression = ({ open, pageSection, pathname, user }) => {
  useEffect(() => {
    if (!open) return undefined;

    // A menu that closes inside the delay was passed over, not seen, so the timer is the whole
    // condition — the cleanup cancels it on close.
    const timer = setTimeout(() => {
      if (impressionPageKey !== pathname) {
        impressionPageKey = pathname;
        firedImpressions.clear();
      }
      if (firedImpressions.has(pageSection)) return;
      firedImpressions.add(pageSection);
      postListingMenuImpressionEvent(user, { pageSection });
    }, IMPRESSION_DELAY);

    return () => clearTimeout(timer);
  }, [open, pageSection, pathname, user]);
};

/** Tracks a fixed-position anchor while the flyout is open, since the rail itself is fixed. */
const useRect = (getNode, active) => {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!active) return undefined;

    const measure = () => {
      const node = getNode();
      setRect(node ? node.getBoundingClientRect() : null);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, getNode]);

  return rect;
};

/**
 * Rendered inside the header so the header's stacking context keeps it beneath the CTA, but
 * sized to the whole header bar rather than to whichever column it happens to sit in.
 */
export const PostListingHeaderScrim = () => {
  const activePlacement = usePostListingMenuPlacement();
  const anyOpen = !!activePlacement;
  const getHeader = useCallback(() => document.querySelector('header'), []);
  const headerRect = useRect(getHeader, anyOpen);

  if (!headerRect) return null;

  return (
    <ChromeScrim
      $open={anyOpen}
      onClick={() => setOpenPlacement(null)}
      style={{
        top: headerRect.top,
        left: headerRect.left,
        width: headerRect.width,
        height: headerRect.height,
      }}
    />
  );
};

export const PostListingMenuScrim = () => {
  const activePlacement = usePostListingMenuPlacement();
  const anyOpen = !!activePlacement;
  const getSider = useCallback(() => document.querySelector('.ant-layout-sider'), []);
  const siderRect = useRect(getSider, anyOpen);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <BodyScrim $open={anyOpen} onClick={() => setOpenPlacement(null)} />
      {siderRect && (
        <RailScrim
          $open={activePlacement === POST_LISTING_MENU_PLACEMENTS.TOP_NAV}
          onClick={() => setOpenPlacement(null)}
          style={{
            top: siderRect.top,
            left: siderRect.left,
            width: siderRect.width,
            height: siderRect.height,
          }}
        />
      )}
    </>,
    document.body,
  );
};

const PostListingMenu = ({
  placement,
  open,
  focusFirstRow,
  panelRef,
  triggerRef,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { t } = useTranslation();
  const navigate = useRouteNavigate();
  const user = useSelector((state) => state.app.loginUser.user);
  const rtl = useSelector((state) => state.app.AppConfig.rtl);
  const { pathname } = useGetLocation();
  const rowRefs = useRef([]);
  const options = useMemo(() => getPostListingOptions({ t }), [t]);
  const isSideMenu = placement === POST_LISTING_MENU_PLACEMENTS.LEFT_NAV;
  const pageSection = isSideMenu ? PAGE_SECTIONS.LEFT_NAV : PAGE_SECTIONS.TOP_NAV;
  const getMenuItem = useCallback(
    () => triggerRef?.current?.closest?.('.ant-menu-item') || triggerRef?.current,
    [triggerRef],
  );
  const itemRect = useRect(getMenuItem, isSideMenu && open);

  useMenuImpression({ open, pageSection, pathname, user });

  useEffect(() => {
    if (open && focusFirstRow) rowRefs.current[0]?.focus();
  }, [focusFirstRow, open]);

  const handleSelect = (event, option) => {
    // The rail panel renders inside an antd menu item, whose own onClick would otherwise toggle
    // the menu straight back open after this row has closed it.
    event.stopPropagation();
    onClose?.();
    addPropertyEvent(user, isSideMenu ? null : pathname.split('/').pop(), isSideMenu, {
      pageSection,
      name: option.analyticsName,
    });
    navigate(option.path);
  };

  const handleKeyDown = (event) => {
    const lastIndex = options.length - 1;
    const currentIndex = rowRefs.current.indexOf(document.activeElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      rowRefs.current[currentIndex >= lastIndex ? 0 : currentIndex + 1]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      rowRefs.current[currentIndex <= 0 ? lastIndex : currentIndex - 1]?.focus();
    }
  };

  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) onClose?.();
  };

  const railPosition = itemRect && {
    top: itemRect.top + itemRect.height / 2,
    ...(rtl ? { right: window.innerWidth - itemRect.left } : { left: itemRect.right }),
  };

  const panel = (
    <MenuPositioner
      ref={panelRef}
      $placement={placement}
      $open={open}
      $rtl={rtl}
      style={isSideMenu ? railPosition || { display: 'none' } : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    >
      {isSideMenu && <MenuCaret $rtl={rtl} />}
      <MenuPanel role="menu" aria-hidden={!open}>
        {options.map((option, index) => (
          <MenuRow
            key={option.key}
            type="button"
            role="menuitem"
            tabIndex={open ? 0 : -1}
            ref={(node) => {
              rowRefs.current[index] = node;
            }}
            onClick={(event) => handleSelect(event, option)}
          >
            <RowLead>
              <RowIcon>
                <Icon icon={option.icon} size="30px" />
              </RowIcon>
              <RowLabel>{option.label}</RowLabel>
            </RowLead>
            {option.isNew && (
              <RowTrailing>
                <NewChip $rtl={rtl}>{t('NEW')}</NewChip>
              </RowTrailing>
            )}
          </MenuRow>
        ))}
      </MenuPanel>
    </MenuPositioner>
  );

  return isSideMenu && typeof document !== 'undefined' ? createPortal(panel, document.body) : panel;
};

export default PostListingMenu;
